import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import Divider from '@mui/material/Divider';
import qs from 'query-string';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import {
  Collector,
  CollectorScope,
  CollectorSettingScope,
  CollectorType,
  Maintainer
} from 'graphql/types';
import { useMessages } from 'modules/intl/LocaleIntlProvider';
import { useMeContext } from 'modules/me';
import { formatDateFromIsoString } from 'utils/date-time';
import { collectorInstall, isWithinAnotherApp } from '../../containerApp/index';
import { CollectorIconType } from './AllCollectorsListPage';
import { MenuPopper } from './MenuPopper';
import { useHandleDisconnect } from './Setting/hooks/useOnDisconnect';
const useStyles = makeStyles((theme) => ({
  infoView: {
    width: theme.spacing(25)
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 1.5, 1.5, 1.5),
    flexDirection: 'column'
  },

  collectorIcon: {
    alignSelf: 'center',
    display: 'flex',
    height: theme.spacing(15),
    width: theme.spacing(15),
    borderRadius: theme.spacing(3),
    padding: theme.spacing(2),
    border: `solid 3px ${theme.palette.grey[200]}`
  },
  installButton: {
    width: theme.spacing(15),
    borderRadius: theme.spacing(1)
  },
  bottomnDivider: {
    borderBottomWidth: theme.spacing(0.25),
    width: theme.spacing(25.5),
    color: theme.palette.grey[200]
  },
  connected: {
    display: 'flex',
    margin: theme.spacing(-1.5, 0, 0, 0),
    ...theme.typography.caption,
    fontSize: theme.spacing(1.5),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.success.dark,
    width: 'fit-content',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0, 0.8)
  },
  imageConnectedDiv: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: theme.spacing(1.5)
  },
  textDiv: {
    margin: theme.spacing(0, 1, 2, 4),
    color: theme.palette.text.primary
  },
  avaialability: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body2.fontSize
  },
  scope: {
    fontSize: theme.typography.body2.fontSize
  },
  nextline: {
    whiteSpace: 'pre'
  },
  textdivInstallInfo: {
    margin: theme.spacing(1.5, 1, 2, 4),
    color: theme.palette.text.primary,
    fontSize: theme.typography.caption.fontSize,
    display: 'flex',
    flexDirection: 'column'
  },
  bold: {
    fontWeight: 500
  },
  marginBottom: {
    marginBottom: theme.spacing(1.5)
  },
  span: {
    display: 'flex',
    flexDirection: 'row'
  }
}));

const typeIconStyle = makeStyles((theme) => ({
  icon: {
    alignSelf: 'center !important',
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(2)
  }
}));

type CollectorDetailSidePanelProps = {
  info: Collector | null;
  handleClose: () => void;
  handleDialogOpen: () => void;
  installationScopeSet: Set<string>;
  scopeSet: Set<string>;
  installInformation: {
    userMaintainers: Maintainer[] | undefined;
    userInstallDate: string | undefined;
    tenantMaintainers: Maintainer[] | undefined;
    tenantInstallationDate: string | undefined;
  };
};

const messageKeys = [
  'collectors.configure',
  'collectors.uninstall',
  'collectorSetting.install',
  'collectorSetting.disconnectSmall',
  'collectors.installCopy'
];
export const CollectorDetailSidePanel: React.FC<CollectorDetailSidePanelProps> = ({
  info,
  handleClose,
  handleDialogOpen,
  scopeSet,
  installationScopeSet,
  installInformation
}: CollectorDetailSidePanelProps) => {
  const menuPopperMessages = useMessages(messageKeys);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [
    mouseOverButtonType,
    setMouseOverButtonType
  ] = useState<CollectorScope>('' as CollectorScope);
  const menuAnchorTenantE1 = useRef<HTMLLIElement>(null);
  const menuAnchorUserE1 = useRef<HTMLLIElement>(null);

  const [openTenant, setOpenTenant] = useState(false);
  const [openUser, setOpenUser] = useState(false);

  const isPlugin = useMemo(() => info?.type === CollectorType.Plugin, [
    info?.type
  ]);

  const handleConnectClick = useCallback(
    (scope) => {
      if (!isPlugin) {
        const query = qs.stringify({
          id: info?.id,
          scope: scope
        });
        const { origin } = window.location;
        const url = `${origin}/collector/install?${query}`;
        if (isWithinAnotherApp()) {
          collectorInstall(url);
        } else {
          window.open(`/collector/install?${query}`);
        }
      } else {
        window.open(
          scope === CollectorScope.Tenant
            ? (info?.installURL as string)
            : (info?.userInstallURL as string)
        );
      }
      handleClose();
    },
    [handleClose, info?.id, info?.installURL, info?.userInstallURL, isPlugin]
  );
  const handleConfigurePage = useCallback(() => {
    window.open(info?.configURL as string);
    handleClose();
  }, [handleClose, info]);

  const onMouseOverCompany = useCallback(() => {
    if (installationScopeSet.size > 0) {
      setOpenTenant(true);
      setMouseOverButtonType(CollectorScope.Tenant);
    }
  }, [installationScopeSet]);

  const onMouseOverUser = useCallback(() => {
    if (installationScopeSet?.size > 0) {
      setOpenUser(true);
      setMouseOverButtonType(CollectorScope.User);
    }
  }, [installationScopeSet?.size]);

  const onMouseLeave = useCallback(() => {
    setOpenTenant(false);
    setMouseOverButtonType('' as CollectorScope);
  }, []);

  const onMouseLeaveUser = useCallback(() => {
    setOpenUser(false);
    setMouseOverButtonType('' as CollectorScope);
  }, []);
  const onDisconnect = useHandleDisconnect({
    onClose: handleClose,
    scope: mouseOverButtonType
  });

  const menuItem = useMemo(() => {
    if (mouseOverButtonType === CollectorScope.Tenant) {
      if (installationScopeSet.has(CollectorSettingScope.Tenant)) {
        const modifyCompanyComponents = [];
        info?.configURL &&
          modifyCompanyComponents.push({
            name: menuPopperMessages['collectors.configure'],
            onClick: () => handleConfigurePage(),
            testId: `configure-button-${info?.name}`
          });
        modifyCompanyComponents.push({
          name: menuPopperMessages['collectors.uninstall'],
          onClick: onDisconnect(info as Collector),
          testId: `uninstall-button-${info?.name}`
        });
        return modifyCompanyComponents;
      } else {
        return [
          {
            name: menuPopperMessages['collectorSetting.install'],
            onClick: () => handleConnectClick(CollectorSettingScope.Tenant),
            testId: `install-button-${info?.name}`
          }
        ];
      }
    } else if (mouseOverButtonType === CollectorScope.User) {
      const userArray: {
        name: string;
        onClick: () => void;
        testId: string;
      }[] = [];
      if (installationScopeSet.has(CollectorSettingScope.User)) {
        if (isPlugin) {
          userArray.push({
            name: menuPopperMessages['collectors.installCopy'],
            onClick: () => handleConnectClick(CollectorSettingScope.User),
            testId: `install-user-copy-button-${info?.name}`
          });
        }
        userArray.push({
          name: isPlugin
            ? menuPopperMessages['collectorSetting.disconnectSmall']
            : menuPopperMessages['collectors.uninstall'],
          onClick: isPlugin
            ? handleDialogOpen
            : onDisconnect(info as Collector),
          testId: `uninstall-button-user-${info?.name}`
        });

        return userArray;
      } else {
        return [
          {
            name: menuPopperMessages['collectorSetting.install'],
            onClick: () => handleConnectClick(CollectorSettingScope.User),
            testId: `install-button-${info?.name}`
          }
        ];
      }
    }
    return [];
  }, [
    handleConnectClick,
    handleDialogOpen,
    handleConfigurePage,
    info,
    installationScopeSet,
    isPlugin,
    menuPopperMessages,
    mouseOverButtonType,
    onDisconnect
  ]);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleButtonClose = () => {
    setAnchorEl(null);
  };
  const me = useMeContext();
  const classes = useStyles();

  const installationComponent = useCallback(
    (userId, displayText, date) => {
      return (
        <>
          <Typography
            data-testid={'installedByNameText'}
            variant="body2"
            className={classes.bold}
            key={userId}
          >
            {displayText}
          </Typography>

          <span className={classes.span}>
            <Typography variant="body2" data-testid="installedOnText">
              <FormattedMessage id="installedCollectors.installedOn" />
              &nbsp;
            </Typography>
            <Typography
              className={classes.bold}
              data-testid="installedOnDate"
              variant="body2"
            >
              {formatDateFromIsoString(date || '', me)}
            </Typography>
          </span>
          <br />
        </>
      );
    },
    [classes.bold, classes.span, me]
  );

  const installationDetails = useMemo(() => {
    const userDetails: JSX.Element[] = [];
    const tenantDetails: JSX.Element[] = [];
    installInformation.userMaintainers?.forEach((i) => {
      userDetails.push(
        installationComponent(
          i.userId,
          i.displayText,
          installInformation.userInstallDate
        )
      );
    });
    installInformation.tenantMaintainers?.map((i) => {
      tenantDetails.push(
        installationComponent(
          i.userId,
          i.displayText,
          installInformation.tenantInstallationDate
        )
      );
    });
    return {
      installDetails: [...userDetails, ...tenantDetails]
    };
  }, [
    installInformation.tenantInstallationDate,
    installInformation.tenantMaintainers,
    installInformation.userInstallDate,
    installInformation.userMaintainers,
    installationComponent
  ]);
  return (
    <div className={classes.infoView}>
      <div className={classes.imageContainer}>
        <div className={classes.imageConnectedDiv}>
          <img
            className={classes.collectorIcon}
            src={info?.image.src}
            data-testid="collectorImgIcon"
          />
          {installationScopeSet?.size > 0 && (
            <Typography
              className={classes.connected}
              variant="caption"
              data-testid={'connected-button-' + info?.name}
            >
              <FormattedMessage id="collectors.installed" />
            </Typography>
          )}
        </div>
        <Button
          variant="contained"
          className={classes.installButton}
          data-testid="collectorDetailButton"
          onClick={handleButtonClick}
        >
          {installationScopeSet.size > 0 ? (
            <FormattedMessage id="collectors.modify" />
          ) : (
            <FormattedMessage id="collectors.install" />
          )}
        </Button>

        <MenuPopper
          menuItem={menuItem}
          anchorPopper={menuAnchorTenantE1}
          menuAnchorUserE1={menuAnchorUserE1}
          anchorMenu={anchorEl}
          handleButtonClose={handleButtonClose}
          onMouseOverCompany={onMouseOverCompany}
          onMouseOverUser={onMouseOverUser}
          onMouseLeave={onMouseLeave}
          onMouseLeaveUser={onMouseLeaveUser}
          scopeSet={scopeSet}
          installationScopeSet={installationScopeSet}
          connectClick={handleConnectClick}
          openTenant={openTenant}
          openUser={openUser}
        />
        <CollectorIconType
          type={info?.type || undefined}
          overrideClass={typeIconStyle()}
        />
        <Divider
          orientation="horizontal"
          className={classes.bottomnDivider}
          data-testid="collectorImgDivider"
        ></Divider>
      </div>
      <div className={classes.textDiv}>
        <Typography variant="body2" data-testid="collectorPublishedBy">
          {
            <FormattedMessage
              id="collectors.publishedBy"
              values={{ by: info?.publishedBy }}
            />
          }
        </Typography>
        <Typography variant="body2" data-testid="collectorCategoryText">
          <FormattedMessage
            id="collectors.categoryText"
            values={{ category: info?.category }}
          />
        </Typography>
        <br />
        <Typography variant="body2">
          <Typography
            className={classes.avaialability}
            data-testid="collectorAvailabilityText"
          >
            <FormattedMessage id="collectors.availability" />
          </Typography>
          {(info?.appScope || []).map((i) => (
            <Typography
              key={i}
              className={classNames(classes.scope, classes.nextline)}
              data-testid={`collectorAvailabilityContent${i}`}
            >
              <FormattedMessage id={`collectors.appScope.${i}`} />
            </Typography>
          ))}
        </Typography>
      </div>
      <Divider
        orientation="horizontal"
        className={classNames(classes.bottomnDivider, classes.marginBottom)}
        data-testid="collectorImgDividerInstall"
      ></Divider>
      {installationScopeSet.size > 0 && installInformation !== undefined && (
        <>
          <div className={classes.textdivInstallInfo}>
            <Typography variant="body2" data-testid="collectorInstalledByText">
              <FormattedMessage id="installedCollectors.installedBy" />
            </Typography>
            {installationDetails.installDetails}
          </div>
        </>
      )}
    </div>
  );
};
